import { getMobileStream } from '../webrtc/mobileStream'

// Shared camera aspect-ratio choice, used by the Configure Lines editor and the
// Execute play screen so the camera frame (and therefore the drawn crossing
// line) is the same shape everywhere. Stored in sessionStorage.
const KEY = 'aiCameraAspect'

// Special deviceId meaning "the paired phone camera" — selectable anywhere a
// camera is chosen (aiCameraDeviceId can be a real id or this).
export const MOBILE_CAMERA_ID = 'mobile'

export const CAMERA_ASPECTS = [
    { key: '16:9', label: '16:9', value: 16 / 9 },
    { key: '4:3', label: '4:3', value: 4 / 3 },
    { key: '1:1', label: '1:1', value: 1 },
]

export const DEFAULT_ASPECT = '16:9'

export const loadCameraAspect = () =>
    sessionStorage.getItem(KEY) || DEFAULT_ASPECT

export const saveCameraAspect = (key) => sessionStorage.setItem(KEY, key)

export const aspectValue = (key) =>
    (CAMERA_ASPECTS.find((a) => a.key === key) || CAMERA_ASPECTS[0]).value

// Orientation flips the ratio: landscape uses w:h as-is (16:9), portrait
// inverts it (9:16). Default landscape everywhere (incl. the phone camera).
const ORIENT_KEY = 'aiCameraOrientation'
export const DEFAULT_ORIENTATION = 'landscape'
export const loadCameraOrientation = () =>
    sessionStorage.getItem(ORIENT_KEY) || DEFAULT_ORIENTATION
export const saveCameraOrientation = (o) =>
    sessionStorage.setItem(ORIENT_KEY, o)

// Shown wherever Auto orientation is offered: for auto-rotate to work, the phone
// must be allowed to rotate (its OS screen-rotation lock turned off).
export const AUTO_ORIENTATION_HINT =
    'Auto follows your phone. Turn off its screen-rotation (portrait) lock so it can rotate.'

// Orientation MODE: 'auto' follows the phone's real rotation; 'manual' pins the
// value the user chose on the toggle. Auto is the default.
const MODE_KEY = 'aiCameraOrientationMode'
export const DEFAULT_ORIENTATION_MODE = 'auto'
export const loadOrientationMode = () =>
    sessionStorage.getItem(MODE_KEY) || DEFAULT_ORIENTATION_MODE
export const saveOrientationMode = (m) => sessionStorage.setItem(MODE_KEY, m)

// Fired whenever the effective orientation (or mode) changes, so live surfaces
// (play mode toggle, etc.) can react. The camera feed itself re-reads
// effectiveAspect() every frame in mobileView, so it re-orients without this.
export const ORIENTATION_EVENT = 'ai-camera-orientation'
const fireOrientation = () => {
    try {
        window.dispatchEvent(new Event(ORIENTATION_EVENT))
    } catch (e) {
        /* ignore */
    }
}

// True once the phone has reported its own orientation. The phone is
// authoritative (screen.orientation is reliable); frame-shape guessing is a
// fallback that's wrong on iOS Safari, so it must not override a phone report.
let phoneReported = false
// The most recent real orientation observed (from either source), regardless of
// mode — so switching back to Auto can snap to it without waiting for a new
// rotation event.
let lastAuto = null
export const resetPhoneOrientationReported = () => {
    phoneReported = false
    lastAuto = null
}

const applyAutoOrientation = (o) => {
    if (o !== 'portrait' && o !== 'landscape') return
    lastAuto = o
    if (loadOrientationMode() !== 'auto') return
    if (loadCameraOrientation() === o) return
    saveCameraOrientation(o)
    fireOrientation()
}

// The phone told us its real orientation (most reliable path).
export const setPhoneOrientation = (o) => {
    phoneReported = true
    applyAutoOrientation(o)
}

// Orientation inferred from the incoming frame's width/height. Only used until
// the phone reports its own (Android flips frame dims on rotation; iOS may not).
export const setFrameOrientation = (o) => {
    if (phoneReported) return
    applyAutoOrientation(o)
}

// Manual override from the UI toggle: pin orientation and stop auto-following.
export const setManualOrientation = (o) => {
    saveOrientationMode('manual')
    saveCameraOrientation(o)
    fireOrientation()
}

// Return to automatic orientation, snapping to the last observed real
// orientation right away (the next phone/frame signal keeps it in sync).
export const enableAutoOrientation = () => {
    saveOrientationMode('auto')
    if (lastAuto && loadCameraOrientation() !== lastAuto)
        saveCameraOrientation(lastAuto)
    fireOrientation()
}

// The effective display aspect (ratio + orientation). Orientation only applies
// to the phone camera (a local webcam is always landscape); so portrait inverts
// the ratio only when the phone is the selected source.
export const effectiveAspect = () => {
    const r = aspectValue(loadCameraAspect())
    const isPhone =
        sessionStorage.getItem('aiCameraDeviceId') === MOBILE_CAMERA_ID
    return isPhone && loadCameraOrientation() === 'portrait' ? 1 / r : r
}

// getUserMedia video constraints for a device + the chosen aspect ratio.
export const cameraConstraints = (deviceId) => {
    const ar = effectiveAspect()
    const video = {
        width: { ideal: 1280 },
        aspectRatio: { ideal: ar },
    }
    if (deviceId) video.deviceId = { exact: deviceId }
    return video
}

// Acquire a camera source by selection:
//   deviceId === MOBILE_CAMERA_ID  -> the paired phone stream (if connected)
//   unset + phone connected        -> the phone stream (sensible default)
//   otherwise                      -> the chosen/local webcam
// Returns a MediaStream. Use stopCameraStream() to release it safely.
export const acquireCameraStream = async (deviceId) => {
    const wantMobile =
        deviceId === MOBILE_CAMERA_ID || (!deviceId && !!getMobileStream())
    if (wantMobile) {
        const m = getMobileStream()
        if (m) return m
        // Phone chosen but not connected yet — fall back to the local camera.
    }
    const realId = deviceId === MOBILE_CAMERA_ID ? undefined : deviceId
    return navigator.mediaDevices.getUserMedia({
        video: cameraConstraints(realId),
        audio: false,
    })
}

// Validate a stored camera deviceId against the machine's ACTUAL cameras.
// A deviceId is used as an `exact` getUserMedia constraint, so a stale id (site
// data from another machine, an imported project, or a camera that was
// unplugged) throws OverconstrainedError and the feed never opens (bug 43).
// Returns the id only when it's a currently-available camera, else '' (default).
// Before camera permission is granted the ids are blank, so we can't validate —
// keep the id then and let getUserMedia prompt / the caller's retry handle it.
export const sanitizeCameraId = async (deviceId) => {
    if (!deviceId || deviceId === MOBILE_CAMERA_ID) return deviceId || ''
    try {
        const list = await navigator.mediaDevices.enumerateDevices()
        const cams = list.filter((d) => d.kind === 'videoinput')
        const haveRealIds = cams.some((d) => d.deviceId)
        if (!haveRealIds) return deviceId
        return cams.some((d) => d.deviceId === deviceId) ? deviceId : ''
    } catch (e) {
        return deviceId
    }
}

// Stop a camera stream's tracks — but NEVER the shared phone stream (the WebRTC
// peer owns it; stopping it would drop the phone connection for everyone).
export const stopCameraStream = (stream) => {
    if (!stream || stream === getMobileStream()) return
    stream.getTracks().forEach((t) => t.stop())
}

// Camera options for any device picker: real webcams + a "Phone Camera" entry.
export const listCameraOptions = async () => {
    let cams = []
    try {
        const list = await navigator.mediaDevices.enumerateDevices()
        cams = list
            .filter((d) => d.kind === 'videoinput')
            .map((d, i) => ({
                deviceId: d.deviceId,
                label: d.label || `Camera ${i + 1}`,
            }))
    } catch (e) {
        /* ignore */
    }
    cams.push({ deviceId: MOBILE_CAMERA_ID, label: 'Phone Camera', mobile: true })
    return cams
}
